/**
 * The Reports page keeps its tab and filters in the URL query so a link
 * reproduces the view. Pure parse/serialise helpers; the defaults are omitted
 * from the URL so a fresh page has a clean address.
 */
export const REPORT_TABS = ['usage', 'models', 'users', 'spenders', 'generations'] as const;
export type ReportTab = (typeof REPORT_TABS)[number];

export const DAY_PRESETS = [7, 30, 90] as const;
export const GENERATION_KINDS = ['video', 'image', 'audio', 'text', 'all'] as const;
export type GenerationKind = (typeof GENERATION_KINDS)[number];
export type GroupBy = 'model' | 'user';

export interface ReportState {
  tab: ReportTab;
  /** A preset window ending today; null when a custom from/to is set. */
  days: number | null;
  from: string;
  to: string;
  q: string;
  provider: string;
  model: string;
  kind: GenerationKind;
  groupBy: GroupBy;
}

export const DEFAULT_DAYS = 30;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const DEFAULT_REPORT_STATE: ReportState = {
  tab: 'usage',
  days: DEFAULT_DAYS,
  from: '',
  to: '',
  q: '',
  provider: '',
  model: '',
  kind: 'video',
  groupBy: 'model',
};

export function parseReportState(params: URLSearchParams): ReportState {
  const tabRaw = params.get('tab');
  const tab = (REPORT_TABS as readonly string[]).includes(tabRaw ?? '')
    ? (tabRaw as ReportTab)
    : 'usage';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const custom = DATE_RE.test(from) && DATE_RE.test(to);
  const daysRaw = Number(params.get('days'));
  const days = custom
    ? null
    : (DAY_PRESETS as readonly number[]).includes(daysRaw)
      ? daysRaw
      : DEFAULT_DAYS;
  const kindRaw = params.get('kind') ?? '';
  return {
    tab,
    days,
    from: custom ? from : '',
    to: custom ? to : '',
    q: params.get('q') ?? '',
    provider: params.get('provider') ?? '',
    model: params.get('model') ?? '',
    kind: (GENERATION_KINDS as readonly string[]).includes(kindRaw)
      ? (kindRaw as GenerationKind)
      : 'video',
    groupBy: params.get('group_by') === 'user' ? 'user' : 'model',
  };
}

export function serializeReportState(state: ReportState): URLSearchParams {
  const out = new URLSearchParams();
  if (state.tab !== 'usage') out.set('tab', state.tab);
  if (state.days === null) {
    out.set('from', state.from);
    out.set('to', state.to);
  } else if (state.days !== DEFAULT_DAYS) {
    out.set('days', String(state.days));
  }
  if (state.q) out.set('q', state.q);
  if (state.provider) out.set('provider', state.provider);
  if (state.model) out.set('model', state.model);
  if (state.kind !== 'video') out.set('kind', state.kind);
  if (state.groupBy !== 'model') out.set('group_by', state.groupBy);
  return out;
}

/** Switching tab keeps the date window and drops the old tab's own filters. */
export function switchTab(state: ReportState, tab: ReportTab): ReportState {
  return {
    ...DEFAULT_REPORT_STATE,
    tab,
    days: state.days,
    from: state.from,
    to: state.to,
  };
}

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The concrete from/to (YYYY-MM-DD, UTC) a state asks the API for. */
export function resolveRange(state: ReportState, now: Date = new Date()): { from: string; to: string } {
  if (state.days === null) return { from: state.from, to: state.to };
  const from = new Date(now.getTime() - state.days * 86_400_000);
  return { from: isoDay(from), to: isoDay(now) };
}
